import { inject, Service } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Api, ApiTypes } from '@app/api';
import { PayloadForWinnerStatistic } from '@core/interfaces/payload-for-winner-statistic.interface';
import { NotificationService } from '@core/providers/notification/notification.service';
import { TopLowPerformingQuestionsBackendResponse } from '@shared/components/overview/foodback-overview/top-low-performing-questions.interface';
import { CONSTANTS } from '@shared/constants';
import {
  DayOfWeekScores,
  HourRangeScores,
  IVenueScores,
  VenueAgeGenderCounts,
  VenueCategoryAverageEntry,
  VenueLanguageDistributionFromBackend,
  VenueStatistics,
} from '@shared/interfaces/analytics.interface';
import { DynamicDateRange } from '@shared/interfaces/date-range.interface';
import { GenderBreakdown, GenderCounts, toGenderCounts } from '@shared/utils/gender';
import { AnalyticsBackendService } from './analytics/analytics-backend.service';
import {
  AnalyticsEndpointPair,
  SharedAnalyticsBackendService,
} from './shared-analytics-backend.service';

/** Reads this service adds on top of the shared set. */
const VENUE_ANALYTICS_ENDPOINTS = {
  statistics: {
    account: Api.surveys.analytics.accounts.statistics,
    venue: Api.surveys.analytics.venues.statistics,
  },
  scores: {
    account: Api.surveys.analytics.accounts.scores,
    venue: Api.surveys.analytics.venues.scores,
  },
  categoryAverages: {
    account: Api.surveys.analytics.accounts.categoryAverages,
    venue: Api.surveys.analytics.venues.categoryAverages,
  },
  categoryScores: {
    account: Api.surveys.analytics.accounts.category.scores,
    venue: Api.surveys.analytics.venues.category.scores,
  },
  categoryDayOfWeek: {
    account: Api.surveys.analytics.accounts.category.scores.distributions.dayOfWeek,
    venue: Api.surveys.analytics.venues.category.scores.distributions.dayOfWeek,
  },
  categoryHourRanges: {
    account: Api.surveys.analytics.accounts.category.scores.distributions.hourRanges,
    venue: Api.surveys.analytics.venues.category.scores.distributions.hourRanges,
  },
  statementsPerformance: {
    account: Api.surveys.analytics.accounts.statements.performance,
    venue: Api.surveys.analytics.venues.statements.performance,
  },
} as const satisfies Record<string, AnalyticsEndpointPair>;

@Service()
export class VenueAnalyticsBackendService extends SharedAnalyticsBackendService {
  private notificationService = inject(NotificationService);

  private defaultWinnerStatisticBody: Pick<PayloadForWinnerStatistic, 'page' | 'limit'> & {
    dateFrom: null;
    dateTo: null;
  } = {
    page: 1,
    limit: 10,
    dateFrom: null,
    dateTo: null,
  };
  getVenueStatistics$(venueUuid: string, dateRange: DynamicDateRange): Observable<VenueStatistics> {
    return this.makeAnalyticsRequest$(VENUE_ANALYTICS_ENDPOINTS.statistics, venueUuid, dateRange);
  }

  getCategoryAverages$(
    venueUuid: string,
    dateRange: DynamicDateRange
  ): Observable<VenueCategoryAverageEntry[]> {
    return this.makeAnalyticsRequest$(
      VENUE_ANALYTICS_ENDPOINTS.categoryAverages,
      venueUuid,
      dateRange
    );
  }

  getVenueOverallExperienceScores$(
    venueUuid: string,
    dateRange: DynamicDateRange
  ): Observable<IVenueScores> {
    return this.getOverallExperienceUuid$(venueUuid).pipe(
      mergeMap(overallExperienceUuid =>
        this.makeAnalyticsRequest$(
          VENUE_ANALYTICS_ENDPOINTS.categoryScores,
          venueUuid,
          dateRange,
          undefined,
          { category: overallExperienceUuid }
        )
      ),
      map(response => ({
        currentCount: response.currentCount.slice(),
        currentScores: response.currentScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
        previousScores: response.previousScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
      }))
    );
  }

  getVenueGenderDistribution$(venueUuid: string): Observable<GenderBreakdown<GenderCounts>> {
    return this.api
      .get(Api.surveys.analytics.venues.demographics.distributions.gender, {
        pathParams: { venueUuid },
      })
      .pipe(
        map(response => ({
          counts: toGenderCounts(response.distribution),
          hidden: response.genderBreakdownHidden,
        }))
      );
  }

  getVenueLanguageDistribution(
    venueUuid: string
  ): Observable<VenueLanguageDistributionFromBackend> {
    return this.api
      .get(Api.surveys.analytics.venues.demographics.distributions.language, {
        pathParams: { venueUuid },
      })
      .pipe(map(response => AnalyticsBackendService.keysToUpperCase(response)));
  }

  getVenueAgeDistribution$(venueUuid: string): Observable<GenderBreakdown<VenueAgeGenderCounts>> {
    return this.api
      .get(Api.surveys.analytics.venues.demographics.distributions.ageGroup.gender, {
        pathParams: { venueUuid },
      })
      .pipe(
        map(response => ({
          counts: Object.fromEntries(
            Object.entries(response.distribution).map(([ageGroup, distribution]) => [
              ageGroup,
              toGenderCounts(distribution),
            ])
          ),
          hidden: response.genderBreakdownHidden,
        }))
      );
  }

  getVenueAverageAge$(venueUuid: string): Observable<ApiTypes.AgeGroupAveragesDto> {
    return this.api.get(Api.surveys.analytics.venues.demographics.averages.ageGroup, {
      pathParams: { venueUuid },
    });
  }

  getVenueDayByDayScores$(
    venueUuid: string,
    dateRange: DynamicDateRange
  ): Observable<DayOfWeekScores> {
    return this.getOverallExperienceUuid$(venueUuid).pipe(
      mergeMap(overallExperienceUuid =>
        this.makeAnalyticsRequest$(
          VENUE_ANALYTICS_ENDPOINTS.categoryDayOfWeek,
          venueUuid,
          dateRange,
          undefined,
          { category: overallExperienceUuid }
        )
      )
    );
  }

  getVenueHourByHourScores$(
    venueUuid: string,
    dateRange: DynamicDateRange
  ): Observable<HourRangeScores> {
    return this.getOverallExperienceUuid$(venueUuid).pipe(
      mergeMap(overallExperienceUuid =>
        this.makeAnalyticsRequest$(
          VENUE_ANALYTICS_ENDPOINTS.categoryHourRanges,
          venueUuid,
          dateRange,
          undefined,
          { category: overallExperienceUuid }
        )
      )
    );
  }

  getVenueScores$(venueUuid: string, dateRange: DynamicDateRange): Observable<IVenueScores> {
    return this.makeAnalyticsRequest$(VENUE_ANALYTICS_ENDPOINTS.scores, venueUuid, dateRange).pipe(
      map(response => ({
        currentCount: response.currentCount.slice(),
        currentScores: response.currentScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
        previousScores: response.previousScores.map(
          AnalyticsBackendService.mapScoreFromBackendToScore
        ),
      }))
    );
  }

  getWinnerStatisticList$(
    venueUuid: string,
    restrictions?: PayloadForWinnerStatistic
  ): Observable<Api.surveys.rewards.venues.PostResult> {
    const objToSend = restrictions || this.defaultWinnerStatisticBody;

    return this.api
      .post(Api.surveys.rewards.venues, {
        pathParams: { venueUuid },
        body: objToSend,
      })
      .pipe(
        catchError((error): Observable<never> => {
          this.reportWinnerStatisticError(error);

          return throwError(() => error);
        })
      );
  }

  /** The error body carries the message under `error`; fall back to the generic copy when it doesn't. */
  private reportWinnerStatisticError(error: { error?: { errorMessage?: string } }): void {
    const message = error?.error?.errorMessage;

    if (message) {
      this.notificationService.errorSnackbar(message);
    } else {
      this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.ERROR_PROCESSING');
    }
  }

  getVenueTopLowPerformingQuestions$(
    venueUuid: string,
    dateRange: DynamicDateRange
  ): Observable<TopLowPerformingQuestionsBackendResponse[]> {
    return this.makeAnalyticsRequest$(
      VENUE_ANALYTICS_ENDPOINTS.statementsPerformance,
      venueUuid,
      dateRange
    );
  }

  private getOverallExperienceUuid$(venueUuid: string): Observable<string> {
    return this.categoriesBackendService
      .getOverallExperienceDetails(venueUuid, CONSTANTS.CONTEXT.VENUE)
      .pipe(
        map(overallExperience => {
          if (!overallExperience) {
            throw new Error(`Venue ${venueUuid} has no overall experience category`);
          }

          return overallExperience.uuid;
        })
      );
  }
}
